/**
 * sse.js — Helper EventSource pour la sync temps réel.
 *
 * Backend : /api/events/stream (cf backend/routes/realtime.py)
 *
 * Le serveur émet :
 * - { type: "connected", client_id, clients_count, ts }       — à la connexion
 * - { type: "planning_changed", entity, action, ... }          — sur mutation
 *
 * Anti-collision :
 *   À la connexion, on stocke le client_id reçu dans `window.__mtjSseClientId`
 *   ET on le pousse dans le header `X-Client-Id` de chaque mutation axios
 *   (cf interceptor ci-dessous). Le backend exclut alors l'émetteur du
 *   broadcast pour éviter le double update local.
 *
 * v3/L8 (11/10/2026) — reprise après coupure, sans perte :
 *   Chaque événement porte un numéro `seq` (champ SSE `id:`). Quand le navigateur
 *   se reconnecte tout seul, il renvoie `Last-Event-ID` ; quand ce module recrée la
 *   connexion (fermeture serveur, redéploiement), il passe `?depuis=<dernier seq>`.
 *   Le serveur rejoue alors les événements manqués (tampon des 500 derniers). Un
 *   événement déjà reçu (même numéro) n'est pas redistribué. Si le trou est trop
 *   ancien, le serveur envoie `{ type: "resync" }` : les écrans abonnés sans filtre
 *   (ou qui listent "resync") relisent tout.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL as API } from './api';

// ── Singleton EventSource (un seul par onglet) ────────────────────────────────
let _es = null;
let _clientId = null;
let _listeners = new Set();
let _connectedListeners = new Set();
let _retryCount = 0;
let _retryTimer = null;
// v3/L8 — dernier numéro reçu + numéros récents (anti-doublon du rejeu). Un Set
// borné plutôt qu'un « seq <= dernier » : si le compteur du serveur repart à zéro,
// les nouveaux petits numéros doivent passer, pas être jetés pour toujours.
let _dernierSeq = 0;
const _seqVus = new Set();
const _SEQ_VUS_MAX = 600;

export function _dejaVu(seq) {
  if (typeof seq !== 'number') return false;
  if (_seqVus.has(seq)) return true;
  _seqVus.add(seq);
  if (_seqVus.size > _SEQ_VUS_MAX) {
    // on retire le plus ancien inséré (ordre d'insertion du Set)
    _seqVus.delete(_seqVus.values().next().value);
  }
  if (seq > _dernierSeq) _dernierSeq = seq;
  return false;
}

export function urlFlux() {
  const base = `${API}/api/events/stream`;
  return _dernierSeq > 0 ? `${base}?depuis=${_dernierSeq}` : base;
}
// fin v3/L8

function _connect() {
  if (_es) return;
  try {
    const url = urlFlux(); // v3/L8 : `?depuis=` pour rejouer ce qui a été manqué
    // withCredentials : utile si l'app sert sous le même domaine via nginx
    _es = new EventSource(url, { withCredentials: true });

    _es.onopen = () => {
      _retryCount = 0;
    };

    _es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        // v3/L8 — événement déjà distribué (rejeu qui chevauche le direct) : ignoré.
        if (_dejaVu(data.seq)) return;
        if (data.type === 'resync' && typeof data.dernier_seq === 'number') {
          _dernierSeq = data.dernier_seq;
        }
        if (data.type === 'connected' && data.client_id) {
          _clientId = data.client_id;
          // Expose pour debug + interceptor axios
          if (typeof window !== 'undefined') {
            window.__mtjSseClientId = _clientId;
          }
          _connectedListeners.forEach((fn) => {
            try { fn(_clientId); } catch (e) { /* ignore */ }
          });
        }
        _listeners.forEach((fn) => {
          try { fn(data); } catch (e) { /* ignore */ }
        });
      } catch (e) {
        // ligne non JSON (heartbeat) → ignore
      }
    };

    _es.onerror = () => {
      // EventSource gère sa propre reconnexion auto, mais en cas de fermeture
      // serveur (504, etc) on force un reset après backoff
      if (_es && _es.readyState === EventSource.CLOSED) {
        _es = null;
        _clientId = null;
        if (typeof window !== 'undefined') window.__mtjSseClientId = null;
        const delay = Math.min(30000, 1000 * Math.pow(2, _retryCount));
        _retryCount += 1;
        clearTimeout(_retryTimer);
        _retryTimer = setTimeout(_connect, delay);
      }
    };
  } catch (e) {
    console.warn('[SSE] Connect failed:', e);
  }
}

function _disconnect() {
  if (_es) {
    _es.close();
    _es = null;
  }
  _clientId = null;
  if (typeof window !== 'undefined') window.__mtjSseClientId = null;
  clearTimeout(_retryTimer);
}

// ── Interceptor axios global : push X-Client-Id sur les mutations ────────────
// On l'installe une fois au chargement du module. Permet au backend d'exclure
// l'émetteur du broadcast SSE (anti-collision).
let _interceptorInstalled = false;
function _installInterceptor() {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    const method = (config.method || 'get').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const cid = typeof window !== 'undefined' ? window.__mtjSseClientId : null;
      if (cid) {
        config.headers = { ...(config.headers || {}), 'X-Client-Id': cid };
      }
    }
    return config;
  });
}
_installInterceptor();

/**
 * Hook : s'abonne au flux SSE et reçoit chaque event via callback.
 *
 * @param {(event: object) => void} onEvent   — appelé pour chaque event reçu.
 * @param {string[]} [eventTypes]             — si fourni, filtre par `type`.
 */
export function useSSE(onEvent, eventTypes) {
  // R25 : tous les hooks AVANT tout return conditionnel
  const onEventRef = useRef(onEvent);
  const typesRef = useRef(eventTypes);
  const [clientId, setClientId] = useState(_clientId);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    typesRef.current = eventTypes;
  }, [eventTypes]);

  useEffect(() => {
    _connect();
    const handler = (ev) => {
      const types = typesRef.current;
      // v3/CORRECTIFS (11/10/2026) : `resync` (trou plus ancien que le tampon de
      // rejeu) passe TOUJOURS, quel que soit le filtre : sinon aucun onglet filtré
      // ne relisait et l'écran restait faux après une longue mise en veille.
      if (types && Array.isArray(types) && ev && ev.type !== 'resync' && !types.includes(ev.type)) return;
      const fn = onEventRef.current;
      if (typeof fn === 'function') fn(ev);
    };
    const connHandler = (cid) => setClientId(cid);
    _listeners.add(handler);
    _connectedListeners.add(connHandler);
    if (_clientId) setClientId(_clientId);
    return () => {
      _listeners.delete(handler);
      _connectedListeners.delete(connHandler);
    };
  }, []);

  return { clientId };
}

/**
 * v3/L8 — dernier numéro d'événement reçu (0 si aucun).
 */
export function getDernierSeq() {
  return _dernierSeq;
}

/**
 * Récupère le client_id courant (sync, pour usage hors hook).
 */
export function getSSEClientId() {
  return _clientId;
}

/**
 * Force une déconnexion (pour tests / logout).
 */
export function disconnectSSE() {
  _disconnect();
}
